// src/components/HeroButton.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { Modal, TextField, Button, Typography, CircularProgress, Alert } from '@mui/material';
import { checkEnrollmentStatus, enrollInCourse } from '../../services/courseService';
import { getToken, isTokenValid } from '../../utils/jwtUtils';
import { useNotification } from '../../context/NotificationContext';

const HeroButton = ({ course }) => {
    // const [enrollmentStatus, setEnrollmentStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [inviteError, setInviteError] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [inviteCode, setInviteCode] = useState('');
    const [showInviteModal, setShowInviteModal] = useState(false);
    const { showNotification } = useNotification();
    const navigate = useNavigate();

    // Check if user is logged in
    useEffect(() => {
        const token = getToken();
        setIsAuthenticated(token && isTokenValid(token));
    }, []);

    const handleAccessCourse = async () => {
        setLoading(true); // Start loading
        try {
            const data = await checkEnrollmentStatus(course._id);

            // If enrolled (data is not null)
            if (data) {
                if (data.currentLesson && !data.completed) {
                    navigate(`/course/${course.slug}/lesson/${data.currentLesson}`);
                } else if (data.completed) {
                    navigate(`/course/${course.slug}/completed`);
                    showNotification("Course already completed.", "info");
                } else {
                    showNotification("An error occurred. Please try again.", "error");
                }
            } else {
                // Not enrolled
                if (course?.accessType == "invite") {
                    setShowInviteModal(true); // Show invite modal for invite-based access
                }
                // else if (course?.accessType == "open") {} 
                else {
                    // Auto-enroll for open access
                    const enrollmentResponse = await enrollInCourse(course._id);

                    if (enrollmentResponse?.success) {
                        const lessonId = enrollmentResponse?.data?.currentLesson;
                        if (lessonId) {
                            navigate(`/course/${course.slug}/lesson/${lessonId}`);
                        } else {
                            setInviteError("Course not available");
                        }
                    } else {
                        setInviteError(enrollmentResponse?.message || "An error occured. Please try again.");
                    }
                }
            }
        } catch (error) {
            console.error('Failed to check enrollment status:', error);
        } finally {
            setLoading(false); // Stop loading
        }
    };

    // Handle invite code submission
    const handleInviteSubmit = async () => {
        setInviteError(null); // Clear previous errors
        setLoading(true); // Start loading
        try {
            const enrollmentResponse = await enrollInCourse(course._id, inviteCode);

            if (enrollmentResponse?.success) {
                setShowInviteModal(false);
                const lessonId = enrollmentResponse?.data?.currentLesson;
                if (lessonId) {
                    navigate(`/course/${course.slug}/lesson/${lessonId}`);
                } else {
                    setInviteError("Course not available");
                }
            } else {
                setInviteError(enrollmentResponse?.message || "An error occured");
            }
        } catch (error) {
            setInviteError(error.response?.data?.message || "Something went wrong. Please try again.");
        } finally {
            setLoading(false); // Stop loading
        }
    };


    return (
        <>
            <Box>
                {isAuthenticated ? (
                    <Box
                        component="button"
                        onClick={handleAccessCourse}
                        disabled={loading} // Disable button when loading
                        sx={{
                            // backgroundColor: loading ? 'grey.500' : '#23516E',
                            backgroundColor: '#23516E',
                            color: '#fff',
                            textTransform: 'none',
                            fontSize: '1.1rem',
                            fontWeight: '500',
                            border: 'none',
                            borderRadius: 8,
                            padding: '16px 48px',
                            cursor: loading ? 'not-allowed' : 'pointer',
                            transition: 'background-color 0.5s',
                        }}
                    >
                        {loading ? <CircularProgress size={24} color="inherit" /> : "Access Course"}
                    </Box>

                ) : (
                    <Box
                        component="button"
                        onClick={() => {
                            const returnURL = encodeURIComponent(window.location.pathname);
                            navigate(`/signin?returnURL=${returnURL}`);
                        }}
                        sx={{
                            backgroundColor: 'primary.main',
                            color: '#fff',
                            textTransform: 'none',
                            fontSize: '1.1rem',
                            fontWeight: '500',
                            border: 'none',
                            borderRadius: 8,
                            padding: '16px 32px',
                            cursor: 'pointer',
                            transition: 'background-color 0.3s',
                            '&:hover': {
                                backgroundColor: 'primary.dark',
                            },
                        }}
                    >
                        Sign In to Continue
                    </Box>
                )}
            </Box>
            <Modal
                open={showInviteModal}
                onClose={() => setShowInviteModal(false)}
                aria-labelledby="invite-code-modal"
                aria-describedby="invite-code-modal-description"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backdropFilter: 'blur(4px)', // Adds a soft blur to the background
                }}
            >
                <Box sx={{
                    width: '90vw', // Covers most of the screen
                    maxWidth: '560px', // Limits size on very large screens
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    borderRadius: 3,
                    p: 4,
                    textAlign: 'center',
                }}>
                    <Typography variant="h5" fontWeight="600" sx={{ mb: 2 }}>
                        Enter Invite Code
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Please enter your invite code to gain access to this course.
                    </Typography>
                    {inviteError && (
                        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setInviteError(null)}>
                            {inviteError}
                        </Alert>
                    )}
                    <TextField
                        label="Invite Code"
                        fullWidth
                        value={inviteCode}
                        onChange={(e) => setInviteCode(e.target.value)}
                        variant="outlined"
                        sx={{ mb: 3 }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Button
                            variant="outlined"
                            color="secondary"
                            size="large"
                            onClick={() => setShowInviteModal(false)}
                            sx={{ flex: 1, mr: 1 }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            size="large"
                            onClick={handleInviteSubmit}
                            disabled={loading || !inviteCode.trim()} // Prevent double submissions
                            sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >
                            {loading ? <CircularProgress size={24} color="inherit" /> : "Submit"}
                        </Button>
                    </Box>
                </Box>
            </Modal>
        </>
    );
};

export default HeroButton;
